import type { JSX } from "react";
import type { WebStudioScope } from "../../api/boot";
import type { WebStudioCatalogOption } from "../../api/workspaces";
import { Select } from "../../design";

/**
 * Compact top-bar scope controls, fed by the backend workspace catalog.
 * Organization is a read-only display; workspace, worldview, and
 * environment are catalog-backed selectors that fail closed (disabled,
 * current value only) when the backend did not deliver rows. Every
 * change is reported upward — the shell issues the corresponding select
 * operation and only then rewrites the URL scope from the backend
 * confirmation.
 */
export interface AppScopeControlsProps {
  scope: WebStudioScope;
  /** Backend workspace rows mapped to options; null = catalog not delivered. */
  workspaceOptions: ReadonlyArray<WebStudioCatalogOption> | null;
  /** Backend worldview rows mapped to options; null = catalog not delivered. */
  worldviewOptions: ReadonlyArray<WebStudioCatalogOption> | null;
  /** Backend environment rows mapped to options; null = catalog not delivered. */
  environmentOptions: ReadonlyArray<WebStudioCatalogOption> | null;
  /** True while a scope switch is in flight; all controls lock. */
  switchPending: boolean;
  onSelectWorkspace: (workspaceId: string) => void;
  onSelectWorldview: (worldviewId: string) => void;
  onSelectEnvironment: (environmentId: string) => void;
}

export function AppScopeControls({
  scope,
  workspaceOptions,
  worldviewOptions,
  environmentOptions,
  switchPending,
  onSelectWorkspace,
  onSelectWorldview,
  onSelectEnvironment,
}: AppScopeControlsProps): JSX.Element {
  const worldviewId = scope.worldviewId ?? "";
  return (
    <fieldset className="scope-controls" aria-label="Scope">
      <div className="scope-display">
        <span className="scope-display__label">Organization</span>
        <span className="scope-display__value">{scope.organizationId}</span>
      </div>
      <ScopeSelect
        label="Workspace"
        value={scope.workspaceId}
        options={workspaceOptions}
        disabled={switchPending}
        onSelect={onSelectWorkspace}
      />
      <ScopeSelect
        label="Worldview"
        value={worldviewId}
        options={worldviewOptions}
        disabled={switchPending}
        onSelect={onSelectWorldview}
      />
      <ScopeSelect
        label="Environment"
        value={scope.environmentId}
        options={environmentOptions}
        disabled={switchPending}
        onSelect={onSelectEnvironment}
      />
    </fieldset>
  );
}

/**
 * The current scope value always renders, even when it is not a catalog
 * row (deep links can request scopes the catalog does not list); it is
 * marked "(current)" so the mismatch stays visible instead of silently
 * snapping to the first backend row.
 */
function withCurrentValue(
  options: ReadonlyArray<WebStudioCatalogOption> | null,
  current: string,
): Array<{ value: string; label: string }> {
  const rows = (options ?? []).map((option) => ({ value: option.id, label: option.label }));
  if (current !== "" && !rows.some((row) => row.value === current)) {
    return [{ value: current, label: `${current} (current)` }, ...rows];
  }
  return rows;
}

function ScopeSelect({
  label,
  value,
  options,
  disabled,
  onSelect,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<WebStudioCatalogOption> | null;
  disabled: boolean;
  onSelect: (value: string) => void;
}): JSX.Element {
  return (
    <Select
      className="scope-control-select"
      label={label}
      value={value}
      options={withCurrentValue(options, value)}
      // Fail closed: without backend catalog rows there is nothing to
      // switch to, so the control locks on the current value.
      disabled={disabled || options === null}
      onChange={(event) => {
        const next = event.target.value;
        if (next !== value) {
          onSelect(next);
        }
      }}
    />
  );
}
